import { View, Pressable, TextInput } from "react-native"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { trpc } from "utils/trpc"
import { router } from "expo-router"
import { ar } from "i18n/ar"
import { KeyboardAwareScrollView } from "@src/components/keyboard-aware-scroll-view"
import { createEmployeeInput, EMPLOYEE_ROLES } from "@white-labels/input/src/m5azen"
import { Button, Text } from "./core"
import { sharedStyles } from "@src/utils"

interface EmployeeFormProps {
  id?: number
}

export function EmployeeForm({ id }: EmployeeFormProps) {
  const isEditing = !!id
  const utils = trpc.useUtils()
  const employee = trpc.employees.get.useQuery({ id: id as number }, { enabled: isEditing })

  const { control, handleSubmit, formState } = useForm({
    // @ts-expect-error
    resolver: zodResolver(createEmployeeInput),
    values:
      isEditing && employee.data
        ? {
            name: employee.data.name,
            password: employee.data.password,
            username: employee.data.username,
            role: employee.data.role,
          }
        : { role: "employee" },
  })

  const createEmployee = trpc.employees.create.useMutation()
  const updateEmployee = trpc.employees.update.useMutation()

  const loading = createEmployee.isPending || updateEmployee.isPending
  const { isValid } = formState
  const disabled = !isValid || loading

  const onSubmit = handleSubmit(async (data) => {
    if (isEditing) {
      await updateEmployee.mutateAsync({ id: id as number, ...data })
      utils.employees.get.invalidate({ id: id as number })
    } else {
      await createEmployee.mutateAsync(data)
    }

    utils.employees.list.invalidate()
    router.back()
  })

  return (
    <KeyboardAwareScrollView contentContainerStyle={sharedStyles.contentContainer}>
      <View className="bg-white rounded p-3 shadow-sm border border-gray-100 pb-safe">
        <Text className="text-lg font-bold text-gray-700 text-left mb-2">{ar.name}</Text>
        <Controller
          control={control}
          name="name"
          render={({ field: { onChange, value } }) => (
            <TextInput
              value={value}
              onChangeText={onChange}
              placeholder={ar.enterUsername}
              className="bg-white px-4 py-3 rounded border border-gray-200 text-right mb-3"
              textAlign="left"
              autoCapitalize="none"
            />
          )}
        />

        <Text className="text-lg font-bold text-gray-700 text-left mb-2">{ar.username}</Text>
        <Controller
          control={control}
          name="username"
          render={({ field: { onChange, value } }) => (
            <TextInput
              value={value}
              onChangeText={onChange}
              placeholder={ar.enterUsername}
              className="bg-white px-4 py-3 rounded border border-gray-200 text-right mb-3"
              textAlign="left"
              autoCapitalize="none"
            />
          )}
        />

        {/* <Text className="text-lg font-bold text-gray-700 text-left mb-2">{ar.theSalary}</Text>
          <Controller
            control={control}
            name="salary"
            render={({ field: { onChange, value } }) => (
              <TextInput
                value={value?.toString()}
                onChangeText={onChange}
                placeholder={ar.enterSalary}
                className="bg-white px-4 py-3 rounded border border-gray-200 text-right mb-3"
                textAlign="left"
              />
            )}
          /> */}

        <Text className="text-lg font-bold text-gray-700 text-left mb-2">
          {ar.password} {isEditing && `( ${ar.optional} )`}
        </Text>
        <Controller
          control={control}
          name="password"
          render={({ field: { onChange, value } }) => (
            <TextInput
              value={value}
              onChangeText={onChange}
              placeholder={ar.enterPassword}
              secureTextEntry
              className="bg-white px-4 py-3 rounded border border-gray-200 text-right mb-3"
              textAlign="left"
            />
          )}
        />

        <Text className="text-lg font-bold text-gray-700 text-left mb-2">{ar.role}</Text>
        <View className="flex-row mb-2">
          <Controller
            control={control}
            name="role"
            render={({ field: { onChange, value } }) => (
              <>
                {EMPLOYEE_ROLES.map((role) => (
                  <Pressable
                    key={role}
                    onPress={() => onChange(role)}
                    className={`mr-2 px-4 py-2 rounded border-2 active:opacity-70 mb-3 ${value === role ? "border-blue-500 bg-blue-50" : "border-gray-200 bg-white"}`}
                  >
                    <Text className={`text-left font-medium ${value === role ? "text-blue-700" : "text-gray-700"}`}>{ar[role]}</Text>
                  </Pressable>
                ))}
              </>
            )}
          />
        </View>

        <Button disabled={disabled} onPress={onSubmit} loading={loading}>
          {isEditing ? ar.updateEmployee : ar.createEmployee}
        </Button>
      </View>
    </KeyboardAwareScrollView>
  )
}
